import { AxiosResponse } from "axios";

import { axiosInstance } from "../../../../services/v2/axiosInstance";

/*
	Calendario Claro (2026-11-02). Una petición por vista: el día, la semana, la lista de una clase y la Ocupación de
	Aulas (Campus → Aulas). Antes el día pedía cinco cosas en fila y tardaba ~4 s; «Ver» tardaba 3.5 s.
*/

export interface Ref { id: number; nombre: string; }

export interface Clase {
	horarioId: number;
	codigo: string | null;
	fecha: string;
	/** 0 = lunes … 6 = domingo. */
	dia: number;
	/** Horas de la sesión en décimas (145 = 2:30 pm); la pantalla usa el nombre del horario si se puede leer. */
	inicio: number;
	fin: number;
	nombre: string;
	curso: Ref | null;
	modulo: Ref | null;
	periodo: Ref;
	maestra: { id: number; nombre: string | null; apellido: string | null; mostrar: string | null } | null;
	aula: { id: number; nombre: string; cupo: number } | null;
	edificio: Ref | null;
	minimo: number;
	activos: number;
	/** porAlumno: lo vencido de cada alumna (es por alumna en el plantel; una alumna en dos clases cuenta una vez arriba). */
	vencido?: { alumnos: number; total: number; porAlumno?: { [alumnoId: string]: number } };
	kits?: { alumnos: number; total: number };
	/** null = fecha futura (todavía no hay lista que pasar). */
	lista?: { pasada: boolean; presentes: number; marcados: number } | null;
	/** Solo en el día: nombres de las alumnas activas, para el buscador. */
	alumnos?: string[];
}

export interface DiaDTO { fecha: string; hoy: string; clases: Clase[]; }
export interface SemanaDTO { desde: string; hasta: string; hoy: string; clases: Clase[]; }

export interface AlumnoClase {
	id: number;
	matriculaId: number;
	nombre: string;
	apellido: string;
	vencido: number;
	meses: number;
	kit: number;
	/** true presente, false ausente, null sin marcar. */
	asistencia: boolean | null;
}
export interface AlumnosDTO { horarioId: number; fecha: string; alumnos: AlumnoClase[]; }

export interface AulaOcupacion { id: number; nombre: string; cupo: number; edificio: Ref; }
export interface OcupacionDTO { desde: string; hasta: string; hoy: string; aulas: AulaOcupacion[]; clases: Clase[]; }

const base = (campusId: number) => `/admin/campus/${campusId}/calendario`;

export const calendarioApi = {
	dia: (campusId: number, fecha: string): Promise<AxiosResponse<DiaDTO>> =>
		axiosInstance.get<DiaDTO>(`${base(campusId)}/_dia?fecha=${fecha}`),
	semana: (campusId: number, fecha: string): Promise<AxiosResponse<SemanaDTO>> =>
		axiosInstance.get<SemanaDTO>(`${base(campusId)}/_semana?fecha=${fecha}`),
	alumnos: (campusId: number, horarioId: number, fecha: string): Promise<AxiosResponse<AlumnosDTO>> =>
		axiosInstance.get<AlumnosDTO>(`${base(campusId)}/${horarioId}/_alumnos?fecha=${fecha}`),
	ocupacion: (campusId: number, fecha: string): Promise<AxiosResponse<OcupacionDTO>> =>
		axiosInstance.get<OcupacionDTO>(`${base(campusId)}/_ocupacion?fecha=${fecha}`),
};

/*
	Lista de una clase, recordada mientras la pantalla siga abierta: el globito de morosidad, el de asistencia y el panel
	piden la misma lista y no tiene caso pedirla tres veces.
*/
const recordadas = new Map<string, Promise<AlumnoClase[]>>();
export const alumnosDe = (campusId: number, horarioId: number, fecha: string): Promise<AlumnoClase[]> => {
	const llave = `${campusId}|${horarioId}|${fecha}`;
	let p = recordadas.get(llave);
	if (!p) {
		p = calendarioApi.alumnos(campusId, horarioId, fecha).then((r) => r.data.alumnos);
		p.catch(() => recordadas.delete(llave));
		recordadas.set(llave, p);
	}
	return p;
};
export const olvidarAlumnos = () => recordadas.clear();
